import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Modal,
  TextareaAutosize,
  Typography,
} from "@mui/material";

import fontWeight from "../../../styles/mui/fontWeight";
import {
  displayFlex,
  flexAllCentre,
  flexAllEnd,
} from "../../../styles/mui/styles/display";
import {
  AddBookmarkButton,
  CustomTextarea,
  DescriptionFormControl,
  ModalContainer,
  RemoveButton,
  TextfieldStyle,
  TimeFormControl,
  VideoTextfield,
} from "../styles/style";
import { ButtonCloseStyle } from "../../../app/styles/styles";
import { colors } from "../../../styles/colors";
import { FormInputText } from "../../../components/FormInputText";
import { rules } from "../../../utils/validation/Validation";
import {
  AddTrainingVideo,
  Bookmark,
  TrainingVideoForm,
} from "../../../models/Training";
import { MyAccountModel } from "../../../models/MyAccount";
import {
  BOOKMARK_ERROR,
  BookmarkDefaultData,
} from "../../../constants/Training";
import { TextfieldLabel } from "../../../app/common/TextLabel";
import { Numbers } from "../../../constants/Referral";
import { getValue } from "../../../utils";
import { AddTrainingVideoResponse } from "../../../models/api/Training";

export interface PropsFromState {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  isEditVideo?: boolean;
  editTrainingVideo?: TrainingVideoForm;
  loggedInUser?: MyAccountModel;
  addTrainingVideoState: AddTrainingVideoResponse;
  editTrainingVideoState: AddTrainingVideo;
}

export interface PropsFromDispatch {
  postTrainingVideo: (payload: FormData) => void;
  getTrainingVideo: () => void;
  putTrainingVideo: (payload: FormData) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AddEditTraining: React.FC<AllProps> = ({
  isOpenModal,
  setIsOpenModal,
  isEditVideo,
  editTrainingVideo,
  loggedInUser,
  postTrainingVideo,
  putTrainingVideo,
  editTrainingVideoState,
  addTrainingVideoState,
}) => {
  const [bookmarkError, setBookmarkError] = useState<string>("");
  const [showRemoveButton, setShowRemoveButton] = useState<boolean>(false);
  const {
    loading: addTrainingVideoLoading,
    response: addTrainingVideoResponse,
  } = addTrainingVideoState;

  const methods = useForm<any>({
    defaultValues: {
      videoID:
        isEditVideo && editTrainingVideo
          ? editTrainingVideo.videoID
          : Numbers.ZERO,
      title: isEditVideo && editTrainingVideo ? editTrainingVideo.title : "",
      description:
        isEditVideo && editTrainingVideo ? editTrainingVideo.description : "",
      file: null,
      createdBy:
        isEditVideo && editTrainingVideo
          ? editTrainingVideo.createdBy
          : loggedInUser?.providerPortalUser?.email,
      bookmarks:
        isEditVideo && editTrainingVideo
          ? editTrainingVideo.bookmarks
          : [BookmarkDefaultData],
    },
  });

  const { control, handleSubmit } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bookmarks",
  });

  const bookmarks = useWatch({
    control,
    name: "bookmarks",
  });

  useEffect(() => {
    const isBookmarkValid = bookmarks.every(
      (bookmark: Bookmark) =>
        bookmark &&
        typeof bookmark.time === "string" &&
        typeof bookmark.description === "string" &&
        bookmark.time.trim() &&
        bookmark.description.trim()
    );

    if (isBookmarkValid) {
      setBookmarkError("");
    }
  }, [bookmarks]);

  useEffect(() => {
    if (
      isEditVideo &&
      getValue(editTrainingVideo, "bookmarks", []).length > Numbers.ONE
    ) {
      setShowRemoveButton(true);
    }
  }, [isEditVideo]);

  useEffect(() => {
    if (
      getValue(editTrainingVideoState, "videoId", Numbers.ZERO) > Numbers.ZERO
    ) {
      setIsOpenModal(false);
    }
  }, [editTrainingVideoState]);

  useEffect(() => {
    if (
      getValue(addTrainingVideoResponse, "videoId", Numbers.ZERO) > Numbers.ZERO
    ) {
      setIsOpenModal(false);
    }
  }, [addTrainingVideoResponse]);

  const onSubmit = (data: any) => {
    setBookmarkError("");
    const nonEmptyBookmarks = data.bookmarks.filter(
      (bookmark: Bookmark) =>
        bookmark.time.trim() !== "" || bookmark.description.trim() !== ""
    );
    const serializedBookmarks = nonEmptyBookmarks
      .map((bookmark: Bookmark) => `${bookmark.time} - ${bookmark.description}`)
      .join("; ");

    const formData = new FormData();

    formData.append("VideoID", data.videoID);
    formData.append("Title", data.title);
    formData.append("Description", data.description);
    formData.append("File", data.file);
    formData.append("CreatedBy", data.createdBy);
    formData.append("Bookmarks", serializedBookmarks);

    if (isEditVideo) {
      putTrainingVideo(formData);
    } else {
      postTrainingVideo(formData);
    }
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  const handleAddBookmark = () => {
    const isBookmarkValid = bookmarks.every(
      (bookmark: Bookmark) =>
        bookmark.time.trim() && bookmark.description.trim()
    );

    if (isBookmarkValid) {
      append(BookmarkDefaultData);
      setBookmarkError("");
      setShowRemoveButton(true);
    } else {
      setBookmarkError(BOOKMARK_ERROR);
    }
  };

  return (
    <Modal
      open={isOpenModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={flexAllCentre}
    >
      <Grid container sx={ModalContainer}>
        {addTrainingVideoLoading ? (
          <>
            <Grid size={{ xs: 12 }} sx={flexAllCentre} mb={1}>
              <CircularProgress />
            </Grid>
            <Grid size={{ xs: 12 }} sx={flexAllCentre} mb={1}>
              <Typography
                variant="h4"
                color={colors.portalColor[2]}
                fontWeight={fontWeight[3]}
                sx={{ mt: 2 }}
              >
                Video upload is in progress...
              </Typography>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12 }} mb={1}>
              <Typography
                variant="h1"
                color={colors.portalColor[2]}
                fontWeight={fontWeight[3]}
                fontSize={"1.8rem"}
              >
                {isEditVideo ? "Edit Training" : "Add New Training"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} pb={2} pt={1}>
              {TextfieldLabel("Title", true, true)}
              <FormControl variant="standard" fullWidth>
                <FormInputText
                  name="title"
                  control={control}
                  style={TextfieldStyle}
                  helper={rules.title}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pb={2} pt={1}>
              {TextfieldLabel("Description", true, true)}
              <FormControl variant="standard" fullWidth>
                <Controller
                  name="description"
                  control={control}
                  rules={rules.description}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <TextareaAutosize
                        {...field}
                        minRows={2}
                        style={CustomTextarea(error)}
                      />
                      {error && error.message && (
                        <FormHelperText>
                          {error ? error.message : null}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pb={2} pt={1}>
              {TextfieldLabel("Bookmarks", true, true)}
              {fields.map((bookmark: any, index: number) => (
                <Box key={bookmark.id} sx={displayFlex} pb={"0.5rem"}>
                  <FormControl variant="standard" sx={TimeFormControl}>
                    <FormInputText
                      name={`bookmarks.${index}.time`}
                      control={control}
                      style={TextfieldStyle}
                      placeholder="Time (xx:xx)"
                      helper={rules.bookmarkTime}
                    />
                  </FormControl>
                  <FormControl variant="standard" sx={DescriptionFormControl}>
                    <FormInputText
                      name={`bookmarks.${index}.description`}
                      control={control}
                      style={TextfieldStyle}
                      placeholder="Description"
                      helper={rules.bookmarkDescription}
                    />
                  </FormControl>
                  {showRemoveButton && fields.length > Numbers.ONE && (
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      sx={RemoveButton}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
              {bookmarkError && (
                <FormHelperText>{bookmarkError}</FormHelperText>
              )}
              <Button
                variant="contained"
                sx={AddBookmarkButton}
                onClick={handleAddBookmark}
              >
                Add Bookmark
              </Button>
            </Grid>
            {!isEditVideo && (
              <Grid size={{ xs: 12 }} pb={2} pt={1}>
                {TextfieldLabel("Training Video", true, true)}
                <FormControl variant="standard" fullWidth>
                  <FormInputText
                    control={control}
                    name="file"
                    helper={rules.videoFile}
                    inputType="file"
                    accept=".mp4"
                    style={VideoTextfield}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12 }} sx={flexAllEnd} pt={3}>
              <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                Submit
              </Button>
              <Button onClick={handleClose} sx={ButtonCloseStyle}>
                Close
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};

export default AddEditTraining;
